<template>
  <v-container>
    <v-row v-if="!failed" justify="center">
      <v-col cols="4">
        <v-card v-if="!loadingProducts">
          <v-form ref="form" v-model="valid">
            <v-card-title class="headline" primary-title>
              <div class="mb-8">
                Flash sales
                <v-icon color="accent">mdi-arrow-down-circle</v-icon>
              </div>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12" class="py-0">
                  <v-autocomplete label="Brand" v-model="brand" :items="brands" :item-text="'brandName'"
                    :item-value="'brandName'" outlined dense clearable :loading="loading" multiple chips small-chips
                    deletable-chips hide-selected messages="Please select the brand names." @change="updated"
                    :search-input.sync="search">
                  </v-autocomplete>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" class="py-0">
                  <v-autocomplete :rules="[]" label="Condition" v-model="condition" :items="conditions" 
                    outlined dense clearable :loading="loading" chips small-chips deletable-chips>
                  </v-autocomplete>
                </v-col>
              </v-row>
              <v-row>
                  <v-col v-if="showGender" cols="12" class="py-0">
                    <v-autocomplete label="Gender" v-model="gender" :items="genders" outlined dense clearable
                      :loading="loading" chips multiple hide-selected deletable-chips small-chips>
                    </v-autocomplete>
                  </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-autocomplete label="Season" v-model="season" :items="seasons" outlined dense clearable
                    :loading="loading" chips multiple hide-selected deletable-chips small-chips>
                  </v-autocomplete>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" class="py-0">
                  <TreeSelect v-model="category" style="padding-top: 0px; margin-top: 0px; width: 100%" :dropdown-style="{
                    maxHeight: '400px',
                    overflow: 'auto',
                  }" :tree-data="categories" :replaceFields="{
                    children: 'children',
                    title: 'name',
                    key: 'id',
                    value: 'path',
                  }" placeholder="Category" filled allow-clear multiple tree-checkable :maxTagCount="10">
                  </TreeSelect>
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="success" outlined @click="debouncedSetFilters" :disabled="!valid">Continue
                <v-icon right color="success">mdi-arrow-right-bold</v-icon></v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
        <v-card flat v-else>
          <v-card-text>
            <v-row justify="center" class="mt-16">
              <v-col cols="4">
                <h3 class="basic--text">Loading products...</h3>
                <v-progress-circular :size="70" color="accent" indeterminate></v-progress-circular>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <StatusInfo v-if="failed" :responseMessage="responseMessage" :filename="'feeling_blue.svg'" :pending="false"
      :textColor="'error--text'" @close="closeError"></StatusInfo>
  </v-container>
</template>

<script>
import { debounce } from 'lodash';
import { TreeSelect } from "ant-design-vue";
import StatusInfo from "../organisms/StatusInfo";

export default {
  name: "FlashSalesFilter",
  components: {
    TreeSelect,
    StatusInfo,
  },
  props: {},
  data: () => ({
    responseMessage: "",
    debouncedSetFilters: null,
    failed: false,
    brands: [],
    conditions: ["All", "Only defects", "Without defects"],
    categories: [],
    genders: [],
    seasons: ["Winter", "Spring", "Summer", "Fall"],
    brand: "",
    category: undefined,
    condition: "",
    gender: "",
    season: "",
    rules: {
      required: (value) => !!value || "Field is required.",
      requiredMultiple: (value) => value.length > 0 || "Field is required",
    },
    valid: false,
    loading: false,
    loadingProducts: false,
    search: null,
  }),
  created() {
    console.log('🔧 [FlashSalesFilter] Component created');
    this.debouncedSetFilters = debounce(this.setFilters, 500, {
      leading: true,
      trailing: false
    });
  },
  beforeUnmount() {
    console.log('🔧 [FlashSalesFilter] Component unmounting, canceling debounced filters');
    this.debouncedSetFilters.cancel();
  },
  watch: {
    brand() {
      this.search = "";
    },
    showGender(val) {
      if (!val) this.gender = '';
    },
    
  },
  computed: {
    showGender() {
      if (!this.category || this.category.length === 0) return false;
      const clothingKeywords = ['clothing', 'apparel', 'fashion', 'shoes', 'accessories'];
      return this.category.some((cat) =>
        clothingKeywords.some((keyword) =>
          cat.toLowerCase().includes(keyword)
        )
      );
    },
  },
  methods: {
    updated(val) {
      console.log("🔄 [FlashSalesFilter] Brand selection updated:", val);
    },
    /*
        set filter values to send it to backend
    */
    async setFilters() {
      console.log('📝 [FlashSalesFilter] setFilters called');
      console.log('📝 [FlashSalesFilter] Current filter values:', {
        brand: this.brand,
        condition: this.condition,
        category: this.category,
        gender: this.gender,
        season: this.season
      });

      // Emit filters to parent in order to send it to spreadsheet
      this.$emit(
        "getFilters",
        this.brand,
        this.condition,
        this.category,
        this.gender
      );

      if (this.$refs.form.validate()) {
        console.log('✅ [FlashSalesFilter] Form validation passed');

        let filters = { condition: this.condition };

        if (this.brand && this.brand.length > 0) {
          filters.brands = this.brand;
          console.log(`📦 [FlashSalesFilter] Added ${this.brand.length} brands to filters`);
        }

        if (this.category && this.category.length > 0) {
          filters.categories = this.category;
          console.log(`📦 [FlashSalesFilter] Added ${this.category.length} categories to filters`);
        }

        if (this.gender && this.gender.length > 0) {
          filters.gender = this.gender;
          console.log(`📦 [FlashSalesFilter] Added ${this.gender.length} genders to filters`);
        }

        if (this.season && this.season.length > 0) {
          filters.season = this.season;
          console.log(`📦 [FlashSalesFilter] Added ${this.season.length} seasons to filters`);
        }

        console.log('🚀 [FlashSalesFilter] Final filters object:', filters);
        console.log('📊 [FlashSalesFilter] Filters size:', JSON.stringify(filters).length, 'characters');

        this.getProducts(filters);
      } else {
        console.warn('⚠️ [FlashSalesFilter] Form validation failed');
      }
    },
    /*
        get products depending on selected filters
    */
    async getProducts(filters) {
      console.log('🔍 [FlashSalesFilter] getProducts called with filters:', filters);
      this.loadingProducts = true;

      try {
        console.log('📡 [FlashSalesFilter] Dispatching filterFlashSales action...');
        const startTime = Date.now();

        const result = await this.$store.dispatch("filterFlashSales", filters);

        const endTime = Date.now();
        const duration = endTime - startTime;

        console.log(`✅ [FlashSalesFilter] Request completed in ${duration}ms`);
        console.log(`📊 [FlashSalesFilter] Received ${result.data.length} products`);
        console.log('📦 [FlashSalesFilter] First product sample:', result.data[0]);

        // Pass retrieved products to parent component
        this.$emit("getProducts", result.data);

        this.loadingProducts = false;
      } catch (error) {
        console.error('❌ [FlashSalesFilter] Error fetching products:', error);
        console.error('❌ [FlashSalesFilter] Error response:', error.response);
        console.error('❌ [FlashSalesFilter] Error data:', error.response?.data);
        console.error('❌ [FlashSalesFilter] Error status:', error.response?.status);

        this.loadingProducts = false;
        this.failed = true;
        this.responseMessage = error.response?.data
          ? error.response.data
          : "Sorry, something went wrong...";
      }
    },
    /*
        get data to fill dropdowns
    */
    async getDropdowns() {
      console.log('📥 [FlashSalesFilter] Fetching dropdown data...');
      this.loading = true;

      try {
        console.log('📥 [FlashSalesFilter] Fetching brands...');
        const responseBrands = await this.$store.dispatch("getBrands");
        console.log(`✅ [FlashSalesFilter] Fetched ${responseBrands.data.length} brands`);

        console.log('📥 [FlashSalesFilter] Fetching categories...');
        const responseCategories = await this.$store.dispatch("getCategories");
        console.log(`✅ [FlashSalesFilter] Fetched categories`);

        console.log('📥 [FlashSalesFilter] Fetching dropdowns...');
        const responseDropdowns = await this.$store.dispatch("getDropdowns");
        console.log(`✅ [FlashSalesFilter] Fetched dropdowns`);

        this.brands = responseBrands.data;
        this.categories = responseCategories.data;
        this.genders = responseDropdowns.data.gender;
        this.genders = ['Women', 'Men', 'Unisex', 'Kids'];
        console.log('✅ [FlashSalesFilter] All dropdown data loaded successfully');
        this.loading = false;
      } catch (error) {
        console.error('❌ [FlashSalesFilter] Error fetching dropdowns:', error);
        this.loading = false;
      }
    },
    closeError() {
      console.log('🔄 [FlashSalesFilter] Closing error and resetting filters');
      this.failed = false;
      this.brand = "";
      this.condition = "";
      this.category = undefined;
    },
  },
  async mounted() {
    console.log('🎬 [FlashSalesFilter] Component mounted');
    await this.getDropdowns();
  },
};
</script>